import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { Handle, NodeResizer, NodeToolbar, Position } from '@xyflow/react'
import { NODE_DEFAULTS, useFlowStore } from '../../store/flowStore'
import type { FlowNodeData, NodeKind } from '../../types'
import { colorWithAlpha, darkenForBorder } from '../../utils/nodeColor'

export type HandleSpec = { id: string; position: Position }

export const FOUR_HANDLES: HandleSpec[] = [
  { id: 'top', position: Position.Top },
  { id: 'right', position: Position.Right },
  { id: 'bottom', position: Position.Bottom },
  { id: 'left', position: Position.Left },
]

const SWATCHES = [
  '#3b82f6',
  '#22c55e',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#64748b',
]

type NodeShellProps = {
  id: string
  selected: boolean
  data: FlowNodeData
  kind: NodeKind
  handles?: HandleSpec[]
  minWidth?: number
  minHeight?: number
  keepAspectRatio?: boolean
  /** Renderização extra atrás do rótulo (ex.: SVG do losango) */
  shape?: ReactNode
  className?: string
}

/** Cores explícitas no style (não só CSS var) — html-to-image não resolve var() em SVG. */
function shellSurfaceStyle(kind: NodeKind, color: string): CSSProperties {
  const border = darkenForBorder(color)
  switch (kind) {
    case 'decision':
      return {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
      }
    case 'annotation':
      return {
        backgroundColor: colorWithAlpha(color, 0.08),
        borderColor: color,
        borderStyle: 'dashed',
      }
    case 'group':
      return {
        backgroundColor: colorWithAlpha(color, 0.1),
        borderColor: color,
        borderStyle: 'dashed',
      }
    default:
      return {
        backgroundColor: color,
        borderColor: border,
        borderStyle: 'solid',
      }
  }
}

export function NodeShell({
  id,
  selected,
  data,
  kind,
  handles = FOUR_HANDLES,
  minWidth = 100,
  minHeight = 44,
  shape,
  className = '',
}: NodeShellProps) {
  const updateNodeData = useFlowStore((s) => s.updateNodeData)
  const duplicateNodes = useFlowStore((s) => s.duplicateNodes)
  const deleteNodes = useFlowStore((s) => s.deleteNodes)
  const presentationMode = useFlowStore((s) => s.presentationMode)
  const readOnly = useFlowStore((s) => s.readOnly)
  const locked = presentationMode || readOnly

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(data.label)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (editing) {
      textareaRef.current?.focus()
      textareaRef.current?.select()
    }
  }, [editing])

  const startEditing = () => {
    if (locked) return
    setDraft(data.label)
    setEditing(true)
  }

  const commit = () => {
    setEditing(false)
    if (draft !== data.label) updateNodeData(id, { label: draft })
  }

  const nodeColor = data.color ?? NODE_DEFAULTS[kind].color
  const style = {
    '--node-color': nodeColor,
    ...shellSurfaceStyle(kind, nodeColor),
  } as CSSProperties

  return (
    <div
      className={`node-shell kind-${kind} ${className}`.trim()}
      style={style}
      onDoubleClick={startEditing}
    >
      <NodeResizer
        isVisible={selected && !locked}
        minWidth={minWidth}
        minHeight={minHeight}
        lineClassName="resizer-line"
        handleClassName="resizer-handle"
      />

      <NodeToolbar
        isVisible={selected && !locked}
        position={Position.Top}
        // Decisão: sobe mais para não cobrir a ponta de cima (bolinha + rótulo)
        offset={kind === 'decision' ? 48 : 14}
      >
        <div className="node-toolbar-actions nodrag">
          {SWATCHES.map((color) => (
            <button
              key={color}
              type="button"
              className={`swatch ${data.color === color ? 'active' : ''}`}
              style={{ background: color }}
              title="Mudar cor"
              onClick={() => updateNodeData(id, { color })}
            />
          ))}
          <span className="toolbar-sep" />
          <button
            type="button"
            className="tb-btn"
            title="Duplicar (Ctrl+D)"
            onClick={() => duplicateNodes([id])}
          >
            ⧉
          </button>
          <button
            type="button"
            className="tb-btn danger"
            title="Excluir (Delete)"
            onClick={() => deleteNodes([id])}
          >
            🗑
          </button>
        </div>
      </NodeToolbar>

      {shape}

      <div className="node-label">
        {editing ? (
          <textarea
            ref={textareaRef}
            className="node-label-input nodrag nopan"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                commit()
              }
              if (e.key === 'Escape') {
                setDraft(data.label)
                setEditing(false)
              }
            }}
          />
        ) : (
          <span className="node-label-text">{data.label}</span>
        )}
      </div>

      {handles.map((h) => (
        <Handle
          key={h.id}
          id={h.id}
          type="source"
          position={h.position}
          className="app-handle"
          isConnectable={!locked}
          style={{ width: 16, height: 16, border: nodeColor }}
        />
      ))}
    </div>
  )
}
